/**
 * Track component (Task 4.4, enhanced in Task 5.2, 5.4, 5.6, 2.3, 2.4, Task Group 6)
 *
 * Represents a single timeline track where tools can be placed.
 *
 * Features:
 * - Track number label (Track 1, Track 2, etc.)
 * - Standardized 60px height
 * - Alternating background colors for visual distinction
 * - Horizontal time grid matching ruler (0.1s intervals)
 * - Renders ToolBlock components for tools on this track
 * - Renders GroupBlock components for placed groups on this track
 * - Drop zone for accepting tools from sidebar (Task 5.4)
 * - Drop zone for repositioning timeline tools (Task 5.6)
 * - Drop zone for tool groups that place as atomic units (Group Sub-Timeline feature)
 * - Stable selectors to prevent render loops (Task 2.3)
 * - Track existence validation for drops (Task 2.4)
 * - Tool resize via edge dragging (Task Group 4)
 * - Multi-select support via selectedToolIds Set (Tool Groups and Selection feature)
 * - Empty click deselection (Task Group 3)
 *
 * Updated for Parametric Animation Refactor:
 * - Uses targetRotation and easing instead of speed/intensity
 */

import { useMemo, useRef, useCallback, useState, memo } from 'react';
import { useDrop } from 'react-dnd';
import { shallow } from 'zustand/shallow';
import { useTimelineStore, getGroupDuration } from '../../stores/timelineStore';
import { useUiStore } from '../../stores/uiStore';
import { ToolBlock, MIN_VISUAL_WIDTH } from './ToolBlock';
import { GroupBlock } from './GroupBlock';
import { snapToGrid } from './utils/snapToGrid';
import { DND_ITEM_TYPES } from '../sidebar/dndTypes';
import type {
  SidebarToolDragItem,
  GroupDragItem,
  PlacedGroupDragItem,
} from '../sidebar/dndTypes';
import type { TimelineToolDragItem } from './ToolBlock';
import { getBodyPartColor } from '../../utils/bodyPartColors';

interface TrackProps {
  /**
   * Track number for display (1-based)
   */
  trackNumber: number;

  /**
   * Track index for alternating colors (0-based)
   */
  trackIndex: number;

  /**
   * Timeline duration in seconds
   */
  duration: number;

  /**
   * Pixels per second for grid alignment
   * Default: 100px/s
   */
  pixelsPerSecond?: number;

  /**
   * Optional override tools for edit mode
   * When provided, these tools are rendered instead of the main timeline tools.
   * Used by group edit mode to show group's internal tools.
   */
  editModeTools?: import('../../stores/timelineStore').Tool[];

  /**
   * Whether we are in group edit mode
   * When true, placed groups are hidden and drop operations are disabled
   */
  isEditMode?: boolean;
}

/**
 * Determine grid line interval based on zoom level
 * Matches ruler tick spacing logic for visual alignment
 * Aims to keep ~10-20px between grid lines for visual clarity
 */
function getGridInterval(pixelsPerSecond: number): number {
  const targetPixelsBetweenLines = 15;
  const idealInterval = targetPixelsBetweenLines / pixelsPerSecond;

  // Snap to nice round intervals
  if (idealInterval >= 300) return 300; // 5 minutes
  if (idealInterval >= 60) return 60; // 1 minute
  if (idealInterval >= 30) return 30;
  if (idealInterval >= 10) return 10;
  if (idealInterval >= 5) return 5;
  if (idealInterval >= 2) return 2;
  if (idealInterval >= 1) return 1;
  if (idealInterval >= 0.5) return 0.5;
  if (idealInterval >= 0.2) return 0.2;
  return 0.1;
}

/**
 * Generate array of grid line times
 * Interval adapts based on zoom level to prevent DOM explosion at low zoom
 */
function generateGridLines(duration: number, interval: number): number[] {
  const lines: number[] = [];
  const steps = Math.floor(duration / interval);
  for (let i = 0; i <= steps; i++) {
    const time = i * interval;
    // Round to avoid floating point precision issues
    lines.push(Math.round(time * 100) / 100);
  }
  return lines;
}

/**
 * Get background color based on track index (alternating pattern)
 */
function getTrackBackground(trackIndex: number): string {
  return trackIndex % 2 === 0 ? '#1e1e1e' : '#252525';
}

/**
 * Calculate pixel position for a grid line
 */
function getGridLinePosition(time: number, pixelsPerSecond: number): number {
  return time * pixelsPerSecond;
}

export const Track = memo(function Track({
  trackNumber,
  trackIndex,
  duration,
  pixelsPerSecond = 100,
  editModeTools,
  isEditMode = false,
}: TrackProps) {
  const dropZoneRef = useRef<HTMLDivElement>(null);

  /**
   * Stable selector pattern for tools (Task 2.3)
   *
   * Previously, the selector was:
   *   const tools = useTimelineStore((state) =>
   *     state.tools.filter((t) => t.trackNumber === trackNumber)
   *   );
   *
   * This created a new array reference on every store update, causing:
   * 1. Unnecessary re-renders of the Track component
   * 2. useDrop reconfiguration on every update
   * 3. Potential infinite render loops when combined with other unstable selectors
   *
   * Solution: Use a stable selector for the entire tools array, then memoize
   * the filtered result. This ensures the filtered array only changes when
   * the source tools array actually changes.
   *
   * In edit mode, we use editModeTools prop instead of the main timeline tools.
   */
  const allTools = useTimelineStore((state) => state.tools, shallow);
  const trackTools = useMemo(() => {
    // In edit mode, use the provided editModeTools
    if (isEditMode && editModeTools) {
      return editModeTools.filter((t) => t.trackNumber === trackNumber);
    }
    // Normal mode: use main timeline tools
    return allTools.filter((t) => t.trackNumber === trackNumber);
  }, [allTools, trackNumber, isEditMode, editModeTools]);

  const addTool = useTimelineStore((state) => state.addTool);
  const moveTool = useTimelineStore((state) => state.moveTool);
  const moveTools = useTimelineStore((state) => state.moveTools);
  const updateTool = useTimelineStore((state) => state.updateTool);
  const addPlacedGroup = useTimelineStore((state) => state.addPlacedGroup);
  const movePlacedGroup = useTimelineStore((state) => state.movePlacedGroup);

  /**
   * Stable selector for placed groups on this track
   * Follows same pattern as trackTools for efficiency
   */
  const allPlacedGroups = useTimelineStore(
    (state) => state.placedGroups,
    shallow
  );
  const groups = useTimelineStore((state) => state.groups, shallow);
  const trackPlacedGroups = useMemo(
    () => allPlacedGroups.filter((pg) => pg.trackNumber === trackNumber),
    [allPlacedGroups, trackNumber]
  );

  /**
   * Get tracks array for validation (Task 2.4)
   * Used to verify track exists before accepting drops
   */
  const tracks = useTimelineStore((state) => state.tracks, shallow);

  /**
   * Selection is handled internally by each ToolBlock/GroupBlock via useTimelineBlock hook.
   * clearSelection: clears all selections when clicking empty track space.
   */
  const clearSelection = useUiStore((state) => state.clearSelection);

  /**
   * Handle tool update from resize operations and popover edits
   * Wraps updateTool to match ToolBlock's expected callback signature
   */
  const handleToolUpdate = useCallback(
    (
      toolId: string,
      updates: {
        startTime?: number;
        duration?: number;
        targetRotation?: number;
        easing?: string;
      }
    ) => {
      updateTool(toolId, updates);
    },
    [updateTool]
  );

  /**
   * Handle click on empty space to clear selection (Task Group 3)
   *
   * Checks that:
   * 1. The click target is the drop zone itself (not a tool block)
   * 2. This is not the start of a rubber-band drag (future: Task Group 4)
   *
   * Tool blocks call e.stopPropagation() in their click handlers,
   * so clicks on tools won't reach this handler.
   */
  const handleEmptyClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Only clear selection if clicking directly on the drop zone
      // (not on a tool block which would have captured the event)
      // The drop zone element is the current target
      if (e.target === e.currentTarget) {
        clearSelection();
      }
    },
    [clearSelection]
  );

  /**
   * State for snap preview during drag-drop
   * Shows a semi-transparent "ghost" of where the tool will land
   */
  const [snapPreview, setSnapPreview] = useState<{
    left: number;
    width: number;
    color: string;
  } | null>(null);

  /**
   * Handle group placement (Group Sub-Timeline feature)
   *
   * When a tool group is dropped on a track:
   * 1. Calculate drop position and snap to grid
   * 2. Create a PlacedGroup instance (do NOT unpack into individual tools)
   * 3. PlacedGroup references the group definition and maintains atomicity
   *
   * Groups are now atomic units that can be edited via double-click to enter
   * group edit mode, rather than being unpacked on drop.
   */
  const handleGroupDrop = useCallback(
    (groupItem: GroupDragItem, dropPosition: number) => {
      const { group } = groupItem;
      // Place the group as an atomic unit on the current track
      addPlacedGroup(group.id, trackNumber, dropPosition);
    },
    [addPlacedGroup, trackNumber]
  );

  /**
   * Set up drop zone for sidebar tools, timeline tools, and tool groups (Task 5.4, 5.6, Task Group 6)
   *
   * Enhanced with track existence validation (Task 2.4):
   * - canDrop validates target track exists in store before allowing drop
   * - drop handler double-checks track existence before executing
   *
   * Dependencies are stable (Task 2.3):
   * - trackNumber, pixelsPerSecond are primitive props
   * - addTool, moveTool are stable Zustand action references
   * - tracks is used for validation only
   */
  const [{ isOver, canDrop }, drop] = useDrop<
    | SidebarToolDragItem
    | TimelineToolDragItem
    | GroupDragItem
    | PlacedGroupDragItem,
    void,
    { isOver: boolean; canDrop: boolean }
  >(
    () => ({
      accept: [
        DND_ITEM_TYPES.SIDEBAR_TOOL,
        DND_ITEM_TYPES.TIMELINE_TOOL,
        DND_ITEM_TYPES.TOOL_GROUP,
        DND_ITEM_TYPES.PLACED_GROUP,
      ],
      /**
       * Validate track exists before allowing drop (Task 2.4)
       * This blocks drops at the UI layer for non-existent tracks
       */
      canDrop: () => {
        return tracks.some((t) => t.number === trackNumber);
      },
      /**
       * Update snap preview position during hover
       * Shows where the tool will land with grid snapping applied
       */
      hover: (item, monitor) => {
        const clientOffset = monitor.getClientOffset();
        const dropZoneElement = dropZoneRef.current;

        if (!clientOffset || !dropZoneElement) {
          setSnapPreview(null);
          return;
        }

        // Calculate drop position in pixels
        const dropZoneRect = dropZoneElement.getBoundingClientRect();
        const dropX = clientOffset.x - dropZoneRect.left;

        let snappedLeft: number;
        let toolWidth: number;
        let toolColor: string;

        if (item.type === DND_ITEM_TYPES.SIDEBAR_TOOL) {
          const sidebarItem = item as SidebarToolDragItem;
          const rawTime = dropX / pixelsPerSecond;
          const snappedTime = snapToGrid(Math.max(0, rawTime));
          snappedLeft = snappedTime * pixelsPerSecond;
          const actualWidth =
            sidebarItem.toolDefinition.defaultDuration * pixelsPerSecond;
          toolWidth = Math.max(MIN_VISUAL_WIDTH, actualWidth);
          toolColor = getBodyPartColor(sidebarItem.toolDefinition.bodyPart);
        } else if (item.type === DND_ITEM_TYPES.TIMELINE_TOOL) {
          const timelineItem = item as TimelineToolDragItem;
          // Find the tool to get its duration
          const draggedTool = allTools.find(
            (t) => t.id === timelineItem.toolId
          );
          if (!draggedTool) {
            setSnapPreview(null);
            return;
          }
          const adjustedDropX = dropX - timelineItem.grabOffsetX;
          const adjustedRawTime = adjustedDropX / pixelsPerSecond;
          const adjustedSnappedTime = snapToGrid(Math.max(0, adjustedRawTime));
          snappedLeft = adjustedSnappedTime * pixelsPerSecond;
          const actualWidth = draggedTool.duration * pixelsPerSecond;
          toolWidth = Math.max(MIN_VISUAL_WIDTH, actualWidth);
          toolColor = getBodyPartColor(draggedTool.bodyPart);
        } else if (item.type === DND_ITEM_TYPES.TOOL_GROUP) {
          // For groups, show a preview representing the group span
          const groupItem = item as GroupDragItem;
          const rawTime = dropX / pixelsPerSecond;
          const snappedTime = snapToGrid(Math.max(0, rawTime));
          snappedLeft = snappedTime * pixelsPerSecond;

          // Calculate group duration using the helper function
          const groupDuration = getGroupDuration(groupItem.group);
          const actualWidth = groupDuration * pixelsPerSecond;
          toolWidth = Math.max(MIN_VISUAL_WIDTH, actualWidth);
          // Purple/violet color for groups (matches GroupBlock)
          toolColor = '#8b5cf6';
        } else if (item.type === DND_ITEM_TYPES.PLACED_GROUP) {
          // For placed groups being repositioned
          const placedGroupItem = item as PlacedGroupDragItem;

          // Find the group definition to get duration
          const group = groups.find(
            (g) =>
              allPlacedGroups.find(
                (pg) => pg.id === placedGroupItem.placedGroupId
              )?.groupId === g.id
          );

          if (!group) {
            setSnapPreview(null);
            return;
          }

          const adjustedDropX = dropX - placedGroupItem.grabOffsetX;
          const adjustedRawTime = adjustedDropX / pixelsPerSecond;
          const adjustedSnappedTime = snapToGrid(Math.max(0, adjustedRawTime));
          snappedLeft = adjustedSnappedTime * pixelsPerSecond;

          const groupDuration = getGroupDuration(group);
          const actualWidth = groupDuration * pixelsPerSecond;
          toolWidth = Math.max(MIN_VISUAL_WIDTH, actualWidth);
          toolColor = '#8b5cf6';
        } else {
          setSnapPreview(null);
          return;
        }

        setSnapPreview({
          left: snappedLeft,
          width: toolWidth,
          color: toolColor,
        });
      },
      drop: (item, monitor) => {
        // Clear preview on drop
        setSnapPreview(null);
        /**
         * Double-check track existence in drop handler (Task 2.4)
         * This provides defense-in-depth against race conditions
         */
        const trackExists = useTimelineStore
          .getState()
          .tracks.some((t) => t.number === trackNumber);
        if (!trackExists) {
          console.warn(
            `Drop rejected: Track ${trackNumber} does not exist in store`
          );
          return;
        }

        // Get drop position relative to the track
        const clientOffset = monitor.getClientOffset();
        const dropZoneElement = dropZoneRef.current;

        if (!clientOffset || !dropZoneElement) {
          return;
        }

        // Calculate drop position in pixels
        const dropZoneRect = dropZoneElement.getBoundingClientRect();
        const dropX = clientOffset.x - dropZoneRect.left;

        // Convert pixel position to time and snap to grid
        const rawTime = dropX / pixelsPerSecond;
        const snappedTime = snapToGrid(rawTime);

        // Handle sidebar tool drop (Task 5.4) - Updated for parametric animation
        if (item.type === DND_ITEM_TYPES.SIDEBAR_TOOL) {
          const sidebarItem = item as SidebarToolDragItem;
          addTool({
            toolId: sidebarItem.toolDefinition.id,
            trackNumber: trackNumber,
            startTime: snappedTime,
            duration: sidebarItem.toolDefinition.defaultDuration,
            bodyPart: sidebarItem.toolDefinition.bodyPart,
            targetRotation: 0, // Start at 0 rotation
            easing: sidebarItem.toolDefinition.defaultEasing,
          });
        }
        // Handle timeline tool repositioning (Task 5.6)
        // Adjusted to preserve grab point: subtract grabOffsetX from drop position
        else if (item.type === DND_ITEM_TYPES.TIMELINE_TOOL) {
          const timelineItem = item as TimelineToolDragItem;

          // Calculate adjusted position accounting for where user grabbed the tool
          // dropX is cursor position, grabOffsetX is how far into the tool they grabbed
          // So toolStartX = dropX - grabOffsetX
          const adjustedDropX = dropX - timelineItem.grabOffsetX;
          const adjustedRawTime = adjustedDropX / pixelsPerSecond;
          const adjustedSnappedTime = snapToGrid(
            Math.max(0, adjustedRawTime) // Ensure non-negative
          );

          // Check if this is a multi-selection move
          if (
            timelineItem.selectedTools &&
            timelineItem.selectedTools.length > 1
          ) {
            // Calculate delta from the dragged tool's original position
            const timeDelta =
              adjustedSnappedTime - timelineItem.originalStartTime;
            const trackDelta = trackNumber - timelineItem.originalTrackNumber;

            // Move all selected tools by the same delta
            const moves = timelineItem.selectedTools.map((selectedTool) => ({
              id: selectedTool.id,
              trackNumber: selectedTool.trackNumber + trackDelta,
              startTime: selectedTool.startTime + timeDelta,
            }));

            moveTools(moves);
          } else {
            // Single tool move
            moveTool(timelineItem.toolId, trackNumber, adjustedSnappedTime);
          }
        }
        // Handle tool group drop (Group Sub-Timeline feature)
        else if (item.type === DND_ITEM_TYPES.TOOL_GROUP) {
          const groupItem = item as GroupDragItem;
          handleGroupDrop(groupItem, snappedTime);
        }
        // Handle placed group repositioning
        else if (item.type === DND_ITEM_TYPES.PLACED_GROUP) {
          const placedGroupItem = item as PlacedGroupDragItem;

          // Calculate adjusted position accounting for where user grabbed
          const adjustedDropX = dropX - placedGroupItem.grabOffsetX;
          const adjustedRawTime = adjustedDropX / pixelsPerSecond;
          const adjustedSnappedTime = snapToGrid(
            Math.max(0, adjustedRawTime) // Ensure non-negative
          );

          movePlacedGroup(
            placedGroupItem.placedGroupId,
            trackNumber,
            adjustedSnappedTime
          );
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [
      trackNumber,
      pixelsPerSecond,
      addTool,
      moveTool,
      moveTools,
      movePlacedGroup,
      tracks,
      allTools,
      allPlacedGroups,
      groups,
      handleGroupDrop,
    ]
  );

  // Clear snap preview when not hovering
  // This handles the case when dragging leaves the track
  const clearSnapPreview = useCallback(() => {
    setSnapPreview(null);
  }, []);

  // Combine drop zone ref with useDrop using callback ref pattern
  // This avoids reading the ref during render (React Compiler requirement)
  const setDropRef = useCallback(
    (node: HTMLDivElement | null) => {
      dropZoneRef.current = node;
      drop(node);
    },
    [drop]
  );

  // Memoize grid line generation with adaptive interval based on zoom
  const gridInterval = useMemo(
    () => getGridInterval(pixelsPerSecond),
    [pixelsPerSecond]
  );
  const gridLines = useMemo(
    () => generateGridLines(duration, gridInterval),
    [duration, gridInterval]
  );

  const backgroundColor = getTrackBackground(trackIndex);
  const trackWidth = duration * pixelsPerSecond;

  // Visual feedback for drop zone
  const dropZoneStyle: React.CSSProperties = {
    backgroundColor:
      isOver && canDrop
        ? 'rgba(59, 130, 246, 0.1)' // Blue tint when hovering with tool
        : 'transparent',
    transition: 'background-color 0.15s ease',
  };

  return (
    <div
      className="relative h-[60px] border-b border-gray-700"
      style={{
        backgroundColor,
        width: `${trackWidth}px`,
        minWidth: '100%',
      }}
      data-testid={`track-${trackNumber}`}
    >
      {/* Track label */}
      <div className="absolute left-0 top-0 px-2 py-1 z-10 pointer-events-none">
        <span className="text-xs text-gray-400 font-medium select-none">
          Track {trackNumber}
        </span>
      </div>

      {/* Horizontal time grid lines */}
      {gridLines.map((time) => {
        const position = getGridLinePosition(time, pixelsPerSecond);
        // Major lines: every 5th grid line or at round intervals based on zoom
        const majorInterval =
          gridInterval >= 60 ? 300 : gridInterval >= 1 ? 5 : 1;
        const isMajor = time % majorInterval === 0;

        return (
          <div
            key={`grid-${time}`}
            className="absolute top-0 h-full pointer-events-none"
            style={{
              left: `${position}px`,
              width: '1px',
              backgroundColor: isMajor ? '#404040' : '#303030',
              opacity: isMajor ? 0.5 : 0.3,
            }}
          />
        );
      })}

      {/* Drop zone for tools (Task 5.4, 5.6, Task Group 6) with empty click handler (Task Group 3) */}
      <div
        ref={setDropRef}
        className="absolute inset-0 z-0"
        style={dropZoneStyle}
        data-testid={`track-${trackNumber}-drop-zone`}
        onMouseLeave={clearSnapPreview}
        onClick={handleEmptyClick}
      >
        {/* Snap preview ghost during drag-drop */}
        {isOver && canDrop && snapPreview && (
          <div
            className="absolute top-0 h-full pointer-events-none"
            style={{
              left: `${snapPreview.left}px`,
              width: `${snapPreview.width}px`,
              backgroundColor: snapPreview.color,
              opacity: 0.4,
              borderRadius: 'var(--radius-md)',
              border: `2px dashed ${snapPreview.color}`,
              boxShadow: `0 0 8px ${snapPreview.color}40`,
              transition: 'left 0.05s ease-out',
            }}
            data-testid={`track-${trackNumber}-snap-preview`}
          />
        )}

        {/* Render tool blocks for this track */}
        {/* Note: ToolBlock now manages its own selection state subscription */}
        {trackTools.map((tool) => (
          <ToolBlock
            key={tool.id}
            tool={tool}
            pixelsPerSecond={pixelsPerSecond}
            onUpdate={handleToolUpdate}
          />
        ))}

        {/* Render group blocks for this track (hidden in edit mode) */}
        {!isEditMode &&
          trackPlacedGroups.map((placedGroup) => {
            const group = groups.find((g) => g.id === placedGroup.groupId);
            if (!group) return null;
            return (
              <GroupBlock
                key={placedGroup.id}
                placedGroup={placedGroup}
                group={group}
                pixelsPerSecond={pixelsPerSecond}
              />
            );
          })}
      </div>
    </div>
  );
});
